/**
 * useChampionship — shared hook for championships.
 *
 * Returns:
 *  - champ:  the "primary" active championship (first by startDate) — for
 *            backward-compat with pages that expect a single championship
 *  - champs: ALL championships (active, upcoming, completed)
 *  - active: array of currently-active championships (date-based)
 *  - loading
 *  - selectChamp(id): set the primary champ manually
 *
 * Active = now >= startDate AND (endDate is null OR now <= endDate)
 */
import { useState, useEffect, useCallback } from 'react';
import { db } from '../services/firebase';
import {
  collection, query, orderBy, onSnapshot,
} from 'firebase/firestore';

const champCol = collection(db, 'championships');

export default function useChampionship() {
  const [champs, setChamps] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(champCol, orderBy('startDate', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setChamps(list);
      setLoading(false);
    });
    return unsub;
  }, []);

  // Compute active championships (date-based)
  const now = Date.now();
  const active = champs.filter(c => {
    const start = c.startDate?.toMillis
      ? c.startDate.toMillis()
      : (c.startDate ? new Date(c.startDate).getTime() : null);
    const end = c.endDate?.toMillis
      ? c.endDate.toMillis()
      : (c.endDate ? new Date(c.endDate).getTime() : null);
    if (!start) return false;
    return now >= start && (!end || now <= end);
  });

  // Primary championship: user-selected, or first active, or first in list
  const champ = (selectedId && champs.find(c => c.id === selectedId))
    || active[0]
    || champs[0]
    || null;

  const selectChamp = useCallback((id) => setSelectedId(id), []);

  return { champ, champs, active, loading, selectChamp };
}
